import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Cartao,
  CampoDeTexto,
  EstadoCarregando,
  EstadoVazio,
  MenuDeAcoes,
  Modal,
  Selo,
  useDialogo,
} from '../../componentes/ui';
import {
  CHAVE_DAS_AUTOMACOES,
  carregarAutomacoes,
  carregarExecucoes,
  criarAutomacao,
  editarAutomacao,
  excluirAutomacao,
  interpretarAutomacao,
  regraEmBranco,
  type Automacao,
  type ExecucaoDaAutomacao,
  type Interpretacao,
} from '../../servicos/automacoes';
import { ErroDaApi } from '../ErroDaApi';
import { EditorDeAutomacao, type RascunhoDeAutomacao } from './EditorDeAutomacao';

/** A automação manda este e-mail? (Uma das ações é "Enviar um e-mail" com ele.) */
export function mandaOEmail(a: Automacao, emailId?: string): boolean {
  return (a.definicao?.acoes ?? []).some(
    (acao) => acao.tipo === 'ENVIAR_EMAIL' && (emailId === undefined || acao.emailId === emailId),
  );
}

/** A automação faz algo no CRM (card, tag, comentário, atividade) — e não só manda e-mail? */
export function temAcaoDoCrm(a: Automacao): boolean {
  return (a.definicao?.acoes ?? []).some((acao) => acao.tipo !== 'ENVIAR_EMAIL');
}

/**
 * A aba Automações da Central de Configurações (D-016): as do CRM.
 *
 * As que só mandam e-mail moram na página de cada e-mail (Configurações ›
 * E-mails), junto do texto — um lugar só para tudo o que é de e-mail (D-017).
 *
 * Em cima, a regra nova: a pessoa descreve, a IA diz o que entendeu e o
 * servidor diz como vai executar — e só então ela aprova, corrige por texto ou
 * ajusta nos campos.
 */
export function Automacoes(): JSX.Element {
  const navegar = useNavigate();
  const [rascunho, setRascunho] = useState<RascunhoDeAutomacao | null>(null);
  /** Criou pela IA uma que só manda e-mail: ela não aparece aqui — a tela diz onde está. */
  const [foiParaOEmail, setFoiParaOEmail] = useState<{ nome: string; emailId: string } | null>(
    null,
  );

  return (
    <div className="flex flex-col gap-6">
      <NovaAutomacao
        subtitulo="Descreva o que o Deca deve fazer sozinho no CRM. A IA mostra o que entendeu, e só vale depois que você aprovar. Para mandar e-mail, use a página do e-mail em Configurações › E-mails."
        aoAjustar={setRascunho}
        aoCriarSemIa={() =>
          setRascunho({ nome: '', definicao: regraEmBranco(), ativa: true, pedido: null })
        }
        aoCriar={(a) => {
          const acao = a.definicao?.acoes.find((x) => x.tipo === 'ENVIAR_EMAIL');
          if (!temAcaoDoCrm(a) && acao?.tipo === 'ENVIAR_EMAIL') {
            setFoiParaOEmail({ nome: a.nome, emailId: acao.emailId });
          }
        }}
      />

      {foiParaOEmail !== null && (
        <Aviso
          tom="positivo"
          titulo={`"${foiParaOEmail.nome}" foi criada`}
          aoFechar={() => setFoiParaOEmail(null)}
          acao={
            <Botao
              tamanho="pequeno"
              variante="secundario"
              onClick={() =>
                navegar(
                  `/configuracoes?aba=emails&email=${encodeURIComponent(foiParaOEmail.emailId)}`,
                )
              }
            >
              Abrir o e-mail
            </Botao>
          }
        >
          Ela só manda e-mail, então fica na página do e-mail, em Configurações › E-mails.
        </Aviso>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">As automações</h2>
        <ListaDeAutomacoes
          filtro={temAcaoDoCrm}
          vazio="Descreva acima a primeira, ou crie sem a IA."
          semEmail
        />
      </section>

      <EditorDeAutomacao rascunho={rascunho} aoFechar={() => setRascunho(null)} semEmail />
    </div>
  );
}

/**
 * A lista das automações, com ligar/desligar, editar, renomear, duplicar,
 * histórico e excluir. É a mesma na aba Automações e na página de um e-mail
 * (só com as que mandam aquele e-mail).
 */
export function ListaDeAutomacoes(props: {
  readonly filtro: (a: Automacao) => boolean;
  readonly vazio: string;
  /** Na aba Automações: o editor não oferece "Enviar um e-mail" (isso é da página do e-mail). */
  readonly semEmail?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const [rascunho, setRascunho] = useState<RascunhoDeAutomacao | null>(null);
  const [historico, setHistorico] = useState<Automacao | null>(null);

  const lista = useQuery({ queryKey: CHAVE_DAS_AUTOMACOES, queryFn: carregarAutomacoes });
  const recarregar = (): void =>
    void queryClient.invalidateQueries({ queryKey: CHAVE_DAS_AUTOMACOES });

  const editar = useMutation({
    mutationFn: (v: { id: string; dados: Parameters<typeof editarAutomacao>[1] }) =>
      editarAutomacao(v.id, v.dados),
    onSettled: recarregar,
  });
  const duplicar = useMutation({
    mutationFn: (a: Automacao) =>
      criarAutomacao({
        nome: `${a.nome} (cópia)`.slice(0, 120),
        definicao: a.definicao!,
        // A cópia nasce desligada: duas regras iguais ligadas fariam tudo duas vezes.
        ativa: false,
        pedido: a.pedido,
      }),
    onSettled: recarregar,
  });
  const excluir = useMutation({ mutationFn: excluirAutomacao, onSettled: recarregar });
  const erro = editar.error ?? duplicar.error ?? excluir.error;
  const visiveis = (lista.data ?? []).filter(props.filtro);

  return (
    <div className="flex flex-col gap-3">
      {erro !== null && <ErroDaApi titulo="Não foi possível alterar a automação" erro={erro} />}
      {lista.isError ? (
        <ErroDaApi titulo="Não foi possível carregar as automações" erro={lista.error} />
      ) : lista.data === undefined ? (
        <EstadoCarregando variante="cartao" contexto="as automações" />
      ) : visiveis.length === 0 ? (
        <EstadoVazio titulo="Nenhuma automação" descricao={props.vazio} />
      ) : (
        <ul className="flex flex-col gap-3">
          {visiveis.map((a) => (
            <LinhaDaAutomacao
              key={a.id}
              automacao={a}
              alternando={editar.isPending && editar.variables?.id === a.id}
              aoAlternar={() => editar.mutate({ id: a.id, dados: { ativa: !a.ativa } })}
              aoEditar={() =>
                a.definicao !== null &&
                setRascunho({
                  id: a.id,
                  nome: a.nome,
                  definicao: a.definicao,
                  ativa: a.ativa,
                  pedido: a.pedido,
                })
              }
              aoRenomear={async () => {
                const nome = await dialogo.pedirTexto({
                  titulo: 'Renomear a automação',
                  rotulo: 'Nome',
                  valorInicial: a.nome,
                  obrigatorio: true,
                });
                if (nome !== null && nome.trim() !== '' && nome.trim() !== a.nome) {
                  editar.mutate({ id: a.id, dados: { nome: nome.trim() } });
                }
              }}
              aoDuplicar={() => a.definicao !== null && duplicar.mutate(a)}
              aoVerHistorico={() => setHistorico(a)}
              aoExcluir={async () => {
                const ok = await dialogo.confirmar({
                  titulo: `Excluir "${a.nome}"?`,
                  descricao: a.padrao
                    ? 'Era uma das regras que vinham prontas. Excluída, ela não volta sozinha — para só pausar, desligue.'
                    : 'Ela para de rodar e sai da lista. Para só pausar, desligue.',
                  rotuloConfirmar: 'Excluir',
                  tom: 'perigo',
                });
                if (ok) excluir.mutate(a.id);
              }}
            />
          ))}
        </ul>
      )}

      <EditorDeAutomacao
        rascunho={rascunho}
        aoFechar={() => setRascunho(null)}
        semEmail={props.semEmail === true}
      />
      <HistoricoDaAutomacao automacao={historico} aoFechar={() => setHistorico(null)} />
    </div>
  );
}

/**
 * "Descreva a automação": a IA lê, devolve o que entendeu, e o servidor diz
 * como vai executar. Aprovar cria; "Ajustar nos campos" abre o editor.
 */
export function NovaAutomacao(props: {
  readonly aoAjustar: (rascunho: RascunhoDeAutomacao) => void;
  readonly aoCriarSemIa: () => void;
  readonly titulo?: string;
  readonly subtitulo?: string;
  readonly placeholder?: string;
  /**
   * Vai junto do texto para a IA, sem aparecer no campo — na página de um
   * e-mail, "Mandar o e-mail X", para a pessoa só dizer quando e para quem.
   */
  readonly complemento?: string;
  /** Depois de criada — a aba Automações avisa quando a regra foi para a página de um e-mail. */
  readonly aoCriar?: (automacao: Automacao) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState('');
  const [correcao, setCorrecao] = useState('');
  const [resultado, setResultado] = useState<Interpretacao | null>(null);
  const [pedidos, setPedidos] = useState<string[]>([]);

  const entender = useMutation({
    mutationFn: (v: { texto: string; corrigindo: boolean }) =>
      interpretarAutomacao({
        texto:
          props.complemento === undefined || v.corrigindo
            ? v.texto
            : `${v.texto}\n${props.complemento}`,
        atual:
          v.corrigindo && resultado?.ok === true
            ? { nome: resultado.nome, definicao: resultado.definicao }
            : undefined,
      }),
    onSuccess: (r, v) => {
      setResultado(r);
      if (r.ok) {
        setPedidos((atuais) => (v.corrigindo ? [...atuais, v.texto] : [v.texto]));
        setCorrecao('');
      }
    },
  });
  const criar = useMutation({
    mutationFn: () => {
      if (resultado?.ok !== true) throw new Error('Nada para criar.');
      return criarAutomacao({
        nome: resultado.nome,
        definicao: resultado.definicao,
        pedido: pedidos.join('\n'),
      });
    },
    onSuccess: (criada) => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_DAS_AUTOMACOES });
      props.aoCriar?.(criada);
      setResultado(null);
      setTexto('');
      setPedidos([]);
    },
  });

  const ok = resultado?.ok === true ? resultado : null;

  return (
    <Cartao
      como="section"
      titulo={props.titulo ?? 'Nova automação'}
      subtitulo={
        props.subtitulo ??
        'Descreva o que o Deca deve fazer sozinho. A IA mostra o que entendeu, e só vale depois que você aprovar.'
      }
    >
      <div className="flex flex-col gap-4">
        <CampoDeTexto
          rotulo="Descreva a automação"
          rows={3}
          maxLength={2000}
          placeholder={
            props.placeholder ??
            'Ex.: quando uma negociação do Pontual for concluída, levar o card para Comercial › Concluído e comentar o valor'
          }
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Botao
            carregando={entender.isPending && entender.variables?.corrigindo === false}
            disabled={texto.trim() === ''}
            onClick={() => entender.mutate({ texto: texto.trim(), corrigindo: false })}
          >
            Entender com a IA
          </Botao>
          <Botao variante="sutil" onClick={props.aoCriarSemIa}>
            Criar sem IA
          </Botao>
        </div>

        {entender.isError && <ErroDaApi titulo="A IA não respondeu" erro={entender.error} />}
        {resultado?.ok === false && (
          <Aviso tom="atencao" titulo="Não deu para montar essa automação">
            {resultado.motivo}
          </Aviso>
        )}

        {ok !== null && (
          <div className="flex flex-col gap-4 rounded-card border border-acento-indigo/40 bg-acento-indigo/5 p-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
                Entendi assim
              </span>
              <p className="text-sm text-conteudo-primario">{ok.entendimento}</p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
                Como o Deca vai executar — {ok.nome}
              </span>
              <p className="text-sm text-conteudo-primario">{ok.descricao}</p>
              {ok.problemas.map((p) => (
                <p key={p} className="text-sm text-atencao">
                  {p}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Botao
                carregando={criar.isPending}
                disabled={ok.problemas.length > 0}
                onClick={() => criar.mutate()}
              >
                Aprovar e criar
              </Botao>
              <Botao
                variante="secundario"
                onClick={() =>
                  props.aoAjustar({
                    nome: ok.nome,
                    definicao: ok.definicao,
                    ativa: true,
                    pedido: pedidos.join('\n'),
                    entendimento: ok.entendimento,
                  })
                }
              >
                Ajustar nos campos
              </Botao>
              <Botao variante="sutil" onClick={() => setResultado(null)}>
                Descartar
              </Botao>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <CampoDeTexto
                rotulo="Não é bem isso? Diga o que corrigir"
                className="flex-1"
                rows={2}
                maxLength={2000}
                placeholder="Ex.: só quando o valor for acima de R$ 100 mil"
                value={correcao}
                onChange={(e) => setCorrecao(e.target.value)}
              />
              <Botao
                variante="secundario"
                carregando={entender.isPending && entender.variables?.corrigindo === true}
                disabled={correcao.trim() === ''}
                onClick={() => entender.mutate({ texto: correcao.trim(), corrigindo: true })}
              >
                Corrigir
              </Botao>
            </div>
            {criar.isError && <ErroDaApi titulo="Não foi possível criar" erro={criar.error} />}
          </div>
        )}
      </div>
    </Cartao>
  );
}

const SELO_DA_SITUACAO: Readonly<
  Record<Automacao['situacao'], { tom: 'positivo' | 'neutro' | 'atencao'; rotulo: string }>
> = {
  ATIVA: { tom: 'positivo', rotulo: 'Ligada' },
  DESLIGADA: { tom: 'neutro', rotulo: 'Desligada' },
  PARADA: { tom: 'atencao', rotulo: 'Parada' },
};

const ROTULO_DO_RESULTADO: Readonly<Record<ExecucaoDaAutomacao['resultado'], string>> = {
  APLICADA: 'Aplicada',
  SEM_EFEITO: 'Sem efeito',
  FALHOU: 'Falhou',
};

function LinhaDaAutomacao(props: {
  readonly automacao: Automacao;
  readonly alternando: boolean;
  readonly aoAlternar: () => void;
  readonly aoEditar: () => void;
  readonly aoRenomear: () => void;
  readonly aoDuplicar: () => void;
  readonly aoVerHistorico: () => void;
  readonly aoExcluir: () => void;
}): JSX.Element {
  const a = props.automacao;
  const selo = SELO_DA_SITUACAO[a.situacao];
  const ultima = a.ultimaExecucao;
  return (
    <li className="flex flex-col gap-3 rounded-painel border border-borda bg-superficie-elevada p-4 shadow-cartao">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-conteudo-primario">{a.nome}</h3>
            <Selo tom={selo.tom} tamanho="pequeno">
              {selo.rotulo}
            </Selo>
            {a.padrao && (
              <Selo tom="informativo" tamanho="pequeno" semPonto>
                Veio pronta
              </Selo>
            )}
            {mandaOEmail(a) && temAcaoDoCrm(a) && (
              <Selo tom="progresso" tamanho="pequeno" semPonto>
                Também manda e-mail
              </Selo>
            )}
          </div>
          <p className="text-sm leading-relaxed text-conteudo-secundario">{a.descricao}</p>
          {a.problemas.map((p) => (
            <p key={p} className="text-sm text-atencao">
              {p}
            </p>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Botao
            variante="secundario"
            tamanho="pequeno"
            carregando={props.alternando}
            onClick={props.aoAlternar}
          >
            {a.ativa ? 'Desligar' : 'Ligar'}
          </Botao>
          <Botao
            variante="secundario"
            tamanho="pequeno"
            disabled={a.definicao === null}
            onClick={props.aoEditar}
          >
            Editar
          </Botao>
          <MenuDeAcoes
            rotulo={a.nome}
            itens={[
              { rotulo: 'Renomear', aoEscolher: props.aoRenomear },
              {
                rotulo: 'Duplicar',
                descricao: 'A cópia nasce desligada.',
                aoEscolher: props.aoDuplicar,
              },
              { rotulo: 'Histórico', aoEscolher: props.aoVerHistorico },
              { rotulo: 'Excluir', perigo: true, aoEscolher: props.aoExcluir },
            ]}
          />
        </div>
      </div>
      <p className="text-xs text-conteudo-sutil">
        {ultima === null
          ? 'Ainda não rodou.'
          : `Última vez em ${quando(ultima.quando)}: ${ROTULO_DO_RESULTADO[ultima.resultado].toLowerCase()}${
              ultima.empresa === null ? '' : `, ${ultima.empresa}`
            }. ${a.execucoesNoMes} no último mês.`}
        {a.pedido !== null && a.pedido !== '' && ` Pedido: “${a.pedido.replace(/\n/g, ' / ')}”`}
      </p>
    </li>
  );
}

function HistoricoDaAutomacao(props: {
  readonly automacao: Automacao | null;
  readonly aoFechar: () => void;
}): JSX.Element {
  const id = props.automacao?.id;
  const execucoes = useQuery({
    queryKey: [...CHAVE_DAS_AUTOMACOES, 'execucoes', id],
    queryFn: () => carregarExecucoes(id!),
    enabled: id !== undefined,
  });
  return (
    <Modal
      aberto={props.automacao !== null}
      aoFechar={props.aoFechar}
      titulo={`Histórico — ${props.automacao?.nome ?? ''}`}
      descricao="As últimas 50 vezes que a automação rodou (guardadas por três meses)."
      largura="larga"
      rodape={
        <Botao variante="secundario" onClick={props.aoFechar}>
          Fechar
        </Botao>
      }
    >
      {execucoes.isError ? (
        <ErroDaApi titulo="Não foi possível carregar o histórico" erro={execucoes.error} />
      ) : execucoes.data === undefined ? (
        <EstadoCarregando variante="texto" contexto="o histórico" />
      ) : execucoes.data.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">A automação ainda não rodou.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-borda/60">
          {execucoes.data.map((e, i) => (
            <li key={i} className="flex flex-col gap-1 py-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Selo
                  tom={
                    e.resultado === 'APLICADA'
                      ? 'positivo'
                      : e.resultado === 'FALHOU'
                        ? 'critico'
                        : 'neutro'
                  }
                  tamanho="pequeno"
                >
                  {ROTULO_DO_RESULTADO[e.resultado]}
                </Selo>
                <span className="text-conteudo-primario">{e.empresa ?? '—'}</span>
                <span className="text-xs text-conteudo-sutil">
                  {quando(e.quando)} · {e.evento}
                </span>
              </div>
              <p className="text-sm text-conteudo-secundario">{e.detalhe}</p>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

function quando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
